import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Users,
  Edit,
  Trash2,
  MoreVertical,
  Search,
  FileUp,
  FileDown,
  StickyNote,
  Download,
  BarChart3,
  FileText,
} from "lucide-react";
import { ReactSelect } from "@/components/ui/react-select";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import CustomerSalesReportModal from "../../components/modals/CustomerSalesReportModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Country, State } from "country-state-city";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageContainer } from "@/components/layout/PageContainer";
import { all_routes } from "../../Router/all_routes";
import CustomerModal from "../../components/modals/CustomerModal";
import CustomerNotesModal from "../../components/modals/CustomerNotesModal";

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const Customer = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 500);
  const [modalData, setModalData] = useState({
    id: null,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street_address: "",
    city: "",
    postcode: "",
    house_number: "",
    state: "",
    country: "",
    company: "",
    customer_group_id: "",
    customer_type: "individual",
    heard_about_us: "",
    tax_class: "",
    company_reg_no: "",
    contact_person: "",
    contact_person_phone: "",
    contact_person_email: "",
    contact_person_relation: "",
    email_notifications: false,
    status: "active",
    registration_store_id: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [notesCustomerId, setNotesCustomerId] = useState(null);

  const currentStore = useSelector((state) => state.current_store);
  const [selectedStoreId, setSelectedStoreId] = useState(
    currentStore?.id ? String(currentStore.id) : "",
  );
  const [reportModalCustomer, setReportModalCustomer] = useState(null);
  const [downloadingCustomerId, setDownloadingCustomerId] = useState(null);

  useEffect(() => {
    if (currentStore?.id) {
      setSelectedStoreId(String(currentStore.id));
    }
  }, [currentStore?.id]);

  const rawPermissions = useSelector((state) => state.userpermission_data);
  const permissions = useMemo(() => rawPermissions || [], [rawPermissions]);

  const hasPermission = useCallback(
    (permission) => {
      return permissions && permissions.includes(permission);
    },
    [permissions],
  );

  const handleViewCustomerReport = (rowCustomer) => {
    setReportModalCustomer(rowCustomer);
  };

  const handleDownloadCustomerPdf = async (rowCustomer) => {
    try {
      setDownloadingCustomerId(rowCustomer.id);
      const name =
        rowCustomer.full_name || rowCustomer.company || "Customer";
      toast.info(`Generating sales report PDF for ${name}...`);
      const params = {
        customer_id: String(rowCustomer.id),
      };
      if (selectedStoreId && selectedStoreId !== "all") {
        params.store_id = selectedStoreId;
      }
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.CUSTOMERWISE_SALES,
        {
          params,
          responseType: "blob",
        },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `customer-sales-report-${rowCustomer.id}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Sales report PDF downloaded successfully!");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to download sales report PDF",
      );
    } finally {
      setDownloadingCustomerId(null);
    }
  };

  // Toast helper function

  const openModal = (role = null) => {
    if (role) {
      setModalData(role);
    } else {
      setModalData({
        id: null,
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        street_address: "",
        city: "",
        postcode: "",
        house_number: "",
        state: "",
        country: "",
        company: "",
        customer_group_id: "",
        customer_type: "individual",
        heard_about_us: "",
        tax_class: "",
        company_reg_no: "",
        contact_person: "",
        contact_person_phone: "",
        contact_person_email: "",
        contact_person_relation: "",
        email_notifications: false,
        status: "active",
        registration_store_id: "",
      });
    }
    setIsModalOpen(true);
  };

  const openNotesModal = (id) => {
    setNotesCustomerId(id);
    setIsNotesModalOpen(true);
  };

  // Queries
  const { data: customerData, isLoading: isLoadingCustomers } = useQuery({
    queryKey: [
      "customers",
      currentPage,
      pageSize,
      debouncedSearchText,
      selectedStoreId,
    ],
    queryFn: async () => {
      const params = {
        page: currentPage,
        perPage: pageSize,
      };
      if (debouncedSearchText) {
        params.q = debouncedSearchText;
      }
      if (selectedStoreId && selectedStoreId !== "all") {
        params.registration_store_id = selectedStoreId;
        params.store_id = selectedStoreId;
      }
      const res = await apiClient.get(`${API_BASE_URL}/customer`, {
        params,
      });
      return res.data;
    },
  });

  // Reset to first page when search text or store changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, selectedStoreId]);

  const customer = useMemo(() => customerData?.data || [], [customerData]);
  const pagination = useMemo(
    () => ({
      current_page:
        customerData?.meta?.current_page || customerData?.current_page || 1,
      last_page: customerData?.meta?.last_page || customerData?.last_page || 1,
      per_page: customerData?.meta?.per_page || customerData?.per_page || 10,
      total:
        customerData?.meta?.total ||
        customerData?.total ||
        customerData?.data?.length ||
        0,
      from: customerData?.meta?.from || customerData?.from || 0,
      to: customerData?.meta?.to || customerData?.to || 0,
    }),
    [customerData],
  );

  const { data: groudData = [] } = useQuery({
    queryKey: ["customer-groups"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/customer_group`);
      return res.data.data || [];
    },
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/store`);
      return res.data.data || [];
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(`${API_BASE_URL}/customer/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Customer deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong!");
    },
  });

  const importMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post(
        API_ENDPOINTS.SALES.IMPORT_CUSTOMERS,
        formData,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Customers imported successfully");
      setShowImportModal(false);
      setImportFile(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to import customers");
    },
  });

  const handleDownloadTemplate = async () => {
    try {
      toast.success("Downloading template...");
      const res = await apiClient.get(
        API_ENDPOINTS.SALES.IMPORT_CUSTOMERS_TEMPLATE,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "customer_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  // Event Bus Subscriptions
  useEffect(() => {
    if (!hasPermission("customer read")) {
      toast.error("Unauthorized", {
        description:
          "You do not have permission to view this page. Redirecting to dashboard...",
      });
      setTimeout(() => {
        navigate(all_routes.dashboard);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, hasPermission]);

  const customerTypes = [
    { value: "individual", label: "Individual" },
    { value: "company", label: "Company" },
    { value: "organization", label: "Organization" },
    { value: "government", label: "Government" },
    { value: "non_profit", label: "Non-Profit" },
    { value: "other", label: "Other" },
  ];

  const getCustomerGroupName = (id) => {
    const group = groudData.find((group) => group.id === id);
    return group ? group.name : "";
  };

  const getCustomerTypeName = (type) => {
    const customerType = customerTypes.find((ct) => ct.value === type);
    return customerType ? customerType.label : "Individual";
  };

  const combineAddress = (record) => {
    if (!record.street_address && !record.city) {
      return "";
    }
    return [record.house_number, record.street_address, record.city]
      .filter(Boolean)
      .join(", ");
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteCustomerMutation.mutate(deleteId);
    setIsDeleteDialogOpen(false);
  };

  const getStoreName = (storeId) => {
    const store = stores.find((store) => store.id === storeId);
    return store ? store.name : "-";
  };

  const columns = [
    {
      accessorKey: "full_name",
      header: "Name",
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => handleViewCustomerReport(row.original)}
          className="font-medium text-left text-primary hover:underline cursor-pointer"
          title="Click to view customer sales report"
        >
          {row.original.full_name ||
            `${row.original.first_name || ""} ${row.original.last_name || ""}`.trim() ||
            "-"}
        </button>
      ),
    },
    {
      accessorKey: "registration_store_id",
      header: "Store",
      cell: ({ row }) => getStoreName(row.original.registration_store_id),
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone",
      header: "Phone No.",
      cell: ({ row }) => (
        <span>{row.original.phone !== null ? row.original.phone : "-"}</span>
      ),
    },
    {
      accessorKey: "customer_type",
      header: "Type",
      cell: ({ row }) => (
        <Badge variant="outline">
          {getCustomerTypeName(row.original.customer_type)}
        </Badge>
      ),
    },
    {
      accessorKey: "company",
      header: "Company",
      cell: ({ row }) => row.original.company || "-",
    },
    {
      id: "address",
      header: "Address",
      cell: ({ row }) => combineAddress(row.original) || "-",
    },
    {
      accessorKey: "country",
      header: "Country",
      cell: ({ row }) => row.original.country || "-",
    },
    {
      accessorKey: "customer_group_id",
      header: "Group",
      cell: ({ row }) => getCustomerGroupName(row.original.customer_group_id),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("report customerwise sales view") && (
              <>
                <DropdownMenuItem
                  onClick={() => handleViewCustomerReport(row.original)}
                >
                  <BarChart3 className="mr-2 h-4 w-4" /> Sales Report
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDownloadCustomerPdf(row.original)}
                  disabled={downloadingCustomerId === row.original.id}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {downloadingCustomerId === row.original.id
                    ? "Downloading PDF..."
                    : "Download Sales PDF"}
                </DropdownMenuItem>
              </>
            )}
            {hasPermission("customer update") && (
              <DropdownMenuItem onClick={() => openModal(row.original)}>
                <Edit className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
            )}
            {hasPermission("customer note read") && (
              <DropdownMenuItem onClick={() => openNotesModal(row.original.id)}>
                <StickyNote className="mr-2 h-4 w-4" /> Notes
              </DropdownMenuItem>
            )}
            {hasPermission("customer delete") && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => handleDelete(row.original.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    return customer;
  }, [customer]);

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Customers"
          subtitle="Manage your customer relationships"
        />
        <PageHeaderActions>
          {hasPermission("import customers") && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowImportModal(true)}
              title="Import Customers"
            >
              <FileUp className="h-4 w-4" />
            </Button>
          )}
          {hasPermission("customer create") && (
            <Button onClick={() => openModal()}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Customer
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search customers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="w-full md:w-64">
          <ReactSelect
            options={[
              { value: "", label: "All Stores" },
              ...stores.map((s) => ({ value: String(s.id), label: s.name })),
            ]}
            value={
              selectedStoreId
                ? {
                  value: String(selectedStoreId),
                  label:
                    stores.find(
                      (s) => String(s.id) === String(selectedStoreId),
                    )?.name || "Selected Store",
                }
                : { value: "", label: "All Stores" }
            }
            onChange={(opt) => {
              setSelectedStoreId(opt?.value || "");
              setCurrentPage(1);
            }}
            placeholder="Filter by Store"
            isClearable
          />
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          enableRowSelection={true}
          isLoading={isLoadingCustomers}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize: pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            } else {
              setCurrentPage(updater.pageIndex + 1);
              setPageSize(updater.pageSize);
            }
          }}
          pageCount={pagination.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Customers: {pagination.total}
            </span>
          }
        />
      </TableCard>

      <CustomerSalesReportModal
        open={Boolean(reportModalCustomer)}
        onOpenChange={(open) => !open && setReportModalCustomer(null)}
        customer={reportModalCustomer}
        storeId={selectedStoreId}
      />

      <CustomerModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        initialData={modalData}
      />

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Customers</DialogTitle>
            <DialogDescription>
              Upload a bulk customer file to add multiple customers at once.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Upload a bulk customer file to add multiple customers at once.
              Supported formats: .xlsx, .csv. Download the template to ensure
              your file matches the required format.
            </p>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <FileDown className="mr-2 h-4 w-4" /> Download Template
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Upload File (XLSX, CSV)</Label>
              <Input
                type="file"
                accept=".xlsx,.csv"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => importMutation.mutate(importFile)}
              disabled={!importFile || importMutation.isPending}
            >
              {importMutation.isPending ? "Importing..." : "Import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CustomerNotesModal
        open={isNotesModalOpen}
        onOpenChange={setIsNotesModalOpen}
        customerId={notesCustomerId}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              customer and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default Customer;
